import React, { useState } from "react";
import "./style.sass";
import { useHandler } from "../handler";
import { useSingleton } from "../../../hooks/use-singleton";
import { ResultsReview } from "../result-review";
import assets from "../../../assets";
import { useNavigate } from "react-router-dom";
import { ROUTE_DASHBOARD } from "../../../route/communicative";
import { useNotify } from "../../../hooks/use-notify";
import { LatexRenderer } from "../../../components/latex-renderer";

const LETTERS = ["A", "B", "C", "D", "E"];

const McqQuestion = ({ index, question, selectedAnswer, onSelect }) => {
  return (
    <div className={`grammar-question-card ${selectedAnswer ? "answered" : ""}`}>
      <div className="grammar-question-card__header">
        <span className="question-index-badge">{index}</span>
        <div className="grammar-question-card__prompt">
          <LatexRenderer text={question.prompt} inline />
        </div>
      </div>

      <div className="grammar-question-card__options">
        {question.options.map((option, optIdx) => {
          const isSelected = selectedAnswer === option;
          return (
            <button
              key={option}
              type="button"
              className={`grammar-option-btn ${isSelected ? "selected" : ""}`}
              onClick={() => onSelect(question.id, option)}
            >
              <span className="option-letter-badge">{LETTERS[optIdx] || optIdx + 1}</span>
              <span className="option-text">
                <LatexRenderer text={option} inline />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FillQuestion = ({ index, question, answer, onAnswerChange }) => {
  return (
    <div className={`grammar-question-card ${answer && answer.trim() ? "answered" : ""}`}>
      <div className="grammar-question-card__header">
        <span className="question-index-badge">{index}</span>
        <div className="grammar-question-card__prompt">
          <LatexRenderer text={question.prompt} inline />
        </div>
      </div>

      <div className="grammar-fill-input-wrapper">
        <input
          className="grammar-fill-input"
          type="text"
          placeholder="Type your answer here..."
          value={answer || ""}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
        />
      </div>
    </div>
  );
};

export const Evaluation = ({
  grammarData,
  questionType = "mixed", // "MCQ" | "Fill in the blanks" | "Both"
  topic,
  difficulty,
  onBackToSetup,
}) => {
  const navigate = useNavigate();
  const { toggleNotify } = useNotify();
  const [answers, setAnswers] = useState({});
  const { submitGrammarExercise, loading } = useHandler();
  const questions = grammarData?.questions || [];
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const { mail } = useSingleton();

  let filteredQuestions = [];
  if (questionType === "MCQ") {
    filteredQuestions = questions.filter((q) => q.type === "multiple_choice");
  } else if (questionType === "Fill in the blanks") {
    filteredQuestions = questions.filter((q) => q.type === "fill_blank");
  } else {
    filteredQuestions = questions;
  }

  const answeredCount = filteredQuestions.filter((question) => {
    const answer = answers[question.id];
    return answer !== undefined && answer !== null && answer.toString().trim() !== "";
  }).length;

  const totalQuestions = filteredQuestions.length;
  const isReadyToSubmit = totalQuestions > 0 && answeredCount === totalQuestions;

  const handleSubmit = async () => {
    if (!isReadyToSubmit) {
      toggleNotify({
        type: "error",
        title: "Incomplete Exercise",
        description: `Please answer all questions (${answeredCount}/${totalQuestions} answered).`,
      });
      return;
    }

    try {
      const response = await submitGrammarExercise({
        exerciseId: grammarData.exercise_id,
        userMail: mail,
        answers,
      });

      setResultData(response);
      setShowResult(true);
      toggleNotify({
        type: "success",
        title: "Request Submitted",
        description: "Evaluation submitted successfully!",
      });
    } catch (error) {
      toggleNotify({
        type: "error",
        title: "Request Failed",
        description: "Failed to submit evaluation.",
      });
    }
  };

  if (showResult) {
    return <ResultsReview resultData={resultData} />;
  }

  return (
    <div className="grammar-eval-page">
      <header className="grammar-header">
        <div className="grammar-header__brand">
          <img src={assets.ic_logo} alt="Rovian AI" className="logo" />
          <span className="brand_name">Rovian AI</span>
        </div>
        <div
          className="grammar-header__dashboard-link"
          onClick={() => navigate(ROUTE_DASHBOARD)}
        >
          <span className="back-arrow">←</span>
          <span>Dashboard</span>
        </div>
      </header>

      <main className="grammar-eval-container">
        <div className="grammar-eval-header-bar">
          <div className="header-top-row">
            {onBackToSetup ? (
              <button
                type="button"
                className="grammar-back-to-setup-btn"
                onClick={onBackToSetup}
              >
                ← Change Setup
              </button>
            ) : (
              <div />
            )}
            {(difficulty || topic) && (
              <div className="eval-meta-badges">
                {difficulty && (
                  <span className="eval-badge difficulty-badge">{difficulty}</span>
                )}
                {topic && (
                  <span className="eval-badge topic-badge">{topic}</span>
                )}
              </div>
            )}
          </div>

          <div className="header-main-row">
            <div className="header-title-group">
              <h1 className="grammar-eval-title">Grammar Exercise</h1>
              <p className="grammar-eval-subtitle">
                Answer all questions and submit your answers for an in-depth score and feedback.
              </p>
            </div>
            <div className="grammar-progress-badge">
              <span className="answered-num">{answeredCount}</span>
              <span className="total-num">/ {totalQuestions} answered</span>
            </div>
          </div>
        </div>

        <div className="grammar-questions-list">
          {filteredQuestions.map((question, index) => {
            if (question.type === "multiple_choice") {
              return (
                <McqQuestion
                  key={question.id}
                  index={index + 1}
                  question={question}
                  selectedAnswer={answers[question.id]}
                  onSelect={handleAnswer}
                />
              );
            }

            return (
              <FillQuestion
                key={question.id}
                index={index + 1}
                question={question}
                answer={answers[question.id]}
                onAnswerChange={handleAnswer}
              />
            );
          })}
        </div>

        <div className="grammar-submit-row">
          <button
            type="button"
            className={`submit-btn-eval ${loading ? "loading" : ""}`}
            disabled={!isReadyToSubmit || loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Evaluating Answers...
              </>
            ) : isReadyToSubmit ? (
              `Submit Evaluation (${totalQuestions} Questions)`
            ) : (
              `Answer All Questions (${answeredCount}/${totalQuestions})`
            )}
          </button>
        </div>
      </main>
    </div>
  );
};
